import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { get } from '../apiService';

export interface ResourceProvider {
  id: string;
  name: string;
  npi?: string;
  specialty?: string;
  [key: string]: unknown;
}

export interface ResourceProvidersResponse {
  resourceProviders?: ResourceProvider[] | string[];
  [key: string]: unknown;
}

// The API actually returns an array of strings directly
export type ResourceProvidersArrayResponse = string[];

export const getResourceProviders = async (
  tablespace: string
): Promise<{ data: ResourceProvidersResponse | ResourceProvidersArrayResponse | null; loading: boolean }> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer Id is not available');
  }

  const url = `/resourceMatrix/resource_provider/list?customerId=${customer}&tablespace=${tablespace}`;

  let loading = true;
  try {
    // Validate tablespace before making the call
    if (!tablespace || typeof tablespace !== 'string' || tablespace.trim() === '') {
      throw new Error('Invalid tablespace: tablespace must be a non-empty string');
    }

    const responseData = await get(url);

    // The API returns an array of strings directly, not wrapped in an object
    // So we need to handle both cases: array directly or wrapped in an object
    let data: ResourceProvidersResponse | ResourceProvidersArrayResponse | null = null;

    if (Array.isArray(responseData)) {
      // Response is directly an array of strings
      data = responseData;
    } else if (responseData && typeof responseData === 'object') {
      // Response might be wrapped in an object
      // Check if it has a data property that's an array
      if ('data' in responseData && Array.isArray(responseData.data)) {
        data = responseData.data as ResourceProvidersArrayResponse;
      } else {
        data = responseData as ResourceProvidersResponse;
      }
    } else {
      console.warn('Unexpected response format:', responseData);
      data = null;
    }
    loading = false;
    return { data, loading };
  } catch (err) {
    loading = false;

    // For network errors or API failures, return empty data instead of throwing
    // This allows the calling component to handle gracefully without breaking other features
    if (err instanceof Error) {
      // Only throw for validation errors that should be caught upstream
      if (err.message.includes('Invalid tablespace')) {
        throw err;
      }
      if (err.message.includes('Customer Id')) {
        throw new Error('Authentication error: Customer ID not available');
      }

      // For API/network errors, log warning and return empty data
      // This prevents one API failure from affecting other features
      console.warn('[getResourceProviders] API call failed, returning empty data:', err.message);
      return { data: null, loading: false };
    }

    // Unknown errors - return empty data to prevent breaking other features
    console.warn('[getResourceProviders] Unknown error, returning empty data');
    return { data: null, loading: false };
  }
};

